import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { HeroSection } from "@/components/HeroSection";
import { PickFlipCard } from "@/components/PickFlipCard";
import { BonusPickCard } from "@/components/BonusPickCard";
import { RecentWinnings } from "@/components/RecentWinnings";
import { FanReviews } from "@/components/FanReviews";
import { PricingStrip } from "@/components/PricingStrip";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { Skull, Leaf, Crown, Star } from "lucide-react";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { ConfettiBurst } from "@/components/ConfettiBurst";
import { FlowMeter } from "@/components/FlowMeter";
import { playDingSound } from "@/lib/sound";

export default function Home() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confettiTrigger, setConfettiTrigger] = useState(0);
    const lastUpdatedRef = useRef(null);
    const isFirstLoadRef = useRef(true);

    const fetchPicks = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await api.get("/picks/today");
            const newUpdatedAt = res.data.updated_at;
            if (
                !isFirstLoadRef.current && lastUpdatedRef.current && newUpdatedAt && newUpdatedAt !== lastUpdatedRef.current
            ) {
                setConfettiTrigger((c) => c + 1);
                playDingSound();
            }
            lastUpdatedRef.current = newUpdatedAt;
            isFirstLoadRef.current = false;
            setData(res.data);
        } catch (e) {
            setData({ slots: [], date: "" });
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPicks(); }, [fetchPicks, user]);
    useEffect(() => {
        const id = setInterval(() => fetchPicks(true), 25000);
        return () => clearInterval(id);
    }, [fetchPicks]);

    const slotByKey = (key) => (data?.slots || []).find((s) => s.key === key);
    const RowHeader = ({ icon: Icon, label, color, sub }) => (
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-md" style={{ background: `${color}22`, color }}>
                    <Icon className="w-4 h-4" />
                </span>
                <p className="font-display text-2xl tracking-widest text-white">{label}</p>
                <span className="text-xs font-mono uppercase text-[#8b8ba0] hidden sm:inline">{sub}</span>
            </div>
        </div>
    );

    const renderRow = (keys, labelProps, Component = PickFlipCard) => (
        <div className="mb-10">
            <RowHeader {...labelProps} />
            <div className={`grid grid-cols-1 ${keys.length > 1 ? "sm:grid-cols-2" : ""} gap-5`}>
                {keys.map((k) => {
                    const slot = slotByKey(k);
                    if (loading || !slot) return <Skeleton key={k} className="h-[300px] sm:h-[340px] rounded-2xl pp-bg-1" />;
                    return <Component key={k} slot={slot} isLoggedIn={!!user} />;
                })}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen pp-bg-0 text-[#f4f4f7]">
            <Nav />
            <HeroSection />
            <FlowMeter />

            <section id="daily-picks" className="relative pp-bg-0 py-16 sm:py-24" data-testid="daily-picks-section">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-10">
                        <p className="text-xs font-mono uppercase text-[#a855f7] tracking-widest">Today’s Drop</p>
                        <h2 className="font-display text-3xl sm:text-5xl tracking-widest text-white">TODAY&apos;S 6 PICKS</h2>
                        <p className="mt-1 text-sm text-[#8b8ba0] max-w-xl">{data?.date ? `Date: ${data.date}` : "Loading today’s picks..."} — tap a card to flip and reveal the slip. Bonus pick is Pro+Premium exclusive.</p>
                    </motion.div>

                    {renderRow(["free_1"], { icon: Leaf, label: "FREE — GOBLIN PICK", color: "#22c55e", sub: "For everyone" })}
                    {renderRow(["pro_1", "pro_2"], { icon: Crown, label: "PRO PICKS", color: "#a855f7", sub: "Members only" })}
                    {renderRow(["premium_1", "premium_2"], { icon: Skull, label: "SPORTS PREMIUM — DEMON PLAYS", color: "#ef4444", sub: "High risk • high reward" })}
                    {renderRow(["bonus_1"], { icon: Star, label: "BONUS — HOLO EXCLUSIVE", color: "#facc15", sub: "Pro + Premium subscribers" }, BonusPickCard)}
                </div>
            </section>

            <RecentWinnings />
            <FanReviews />
            <PricingStrip />
            <Footer />
            <HelpRequestButton />
            <ConfettiBurst trigger={confettiTrigger} />
        </div>
    );
}
